import React from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

// Components
import InputField from '../../InputField/MobileView';
import ToggleArrow from '../../ToggleArrow';
import Collapse from '../../Collapse';
import Button from '../../Button';
import Heading from '../../Heading';
import Map from '../../AddressFieldGroup/partials/Map';
import AddressesField from '../../AddressFieldGroup/partials/AddressesField';
import useAddressSelector from '../useAddressSelector';

// State
import { mapLocale as mapLocaleSelector } from '../../../state/modules/map/selectors';

// Utils
import { formatSavedAddress } from '../../../utils/helpers';

// Config
import config from '../../../config';

// Styles
import styles from './AddressSelector.styl';

const cx = classNames.bind(styles);

export default function AddressSelector(props) {
  const { className, onSuccessCallback } = props;

  const intl = useIntl();
  const mapLocale = useSelector(mapLocaleSelector);
  const userCity = useSelector(state => state.city.userCity);
  const list = useSelector(state => state.address.list);

  const {
    buttonRef,
    address,
    isLoading,
    addressInLineValue,
    isIntercomAndPorchOpened,
    setAddressInLineValue,
    //
    handleIntercomAndPorchToggle,
    handleAddressOnChange,
    handleOnBlur,
    handleOnAddressInLineSelect,
    handleSelectSavedAddress,
    handleFlatChange,
    isButtonExist,
    handleOnAccept,
    isError,
  } = useAddressSelector(onSuccessCallback);

  const label = isError
    ? intl.formatMessage({ id: 'field.address.error' })
    : intl.formatMessage({ id: 'field.address.label' });

  const cityCenter = [...userCity.coordinates];

  return (
    <form className={cx('AddressSelector', className)} autoComplete="off">
      <Heading className={cx('AddressSelector__title')} level={3} medium>
        {intl.formatMessage({ id: 'select.address' })}
      </Heading>
      {/* Уточнить на карте */}
      <Map
        className={cx('AddressSelector__map')}
        mapAddressOnChange={handleOnAddressInLineSelect}
        selectedAddress={address.address_in_line}
        center={cityCenter}
        mapLocale={mapLocale}
      />
      <div className={cx('AddressSelector__addresses')}>
        {list.map((addr, i) => (
          <button
            key={addr.id}
            className={cx('AddressSelector__address')}
            onClick={() => handleSelectSavedAddress(addr)}
            type="button"
          >
            {`${formatSavedAddress(addr)}${i === list.length - 1 ? '' : ','}`}
          </button>
        ))}
      </div>
      {/* Адрес */}
      <AddressesField
        className={cx('AddressSelector__addresser')}
        label={label}
        onAddressSelect={handleOnAddressInLineSelect}
        newest
        input={{
          name: 'address_in_line',
          value: addressInLineValue,
          onChange: value => setAddressInLineValue(value),
          required: true,
          placeholder: intl.formatMessage({ id: 'field.address.label' }),
          isError,
        }}
      />
      <div className={cx('AddressSelector__row')}>
        {/* Квартира / офис */}
        <InputField
          name="flat"
          value={address.flat}
          onChange={handleFlatChange}
          placeholder={intl.formatMessage({ id: 'field.flat.label' })}
        />
        {/* Этаж */}
        <InputField
          name="floor"
          value={address.floor}
          onChange={handleAddressOnChange}
          placeholder={intl.formatMessage({ id: 'field.floor.label' })}
          onBlur={handleOnBlur}
        />
      </div>
      <div
        className={cx('AddressSelector__row')}
        style={{
          color: '#C21313',
        }}
      >
        {intl.formatMessage({
          id: 'field.address.flat',
        })}
      </div>
      {/* Указать домофон и подъезд */}
      <div
        className={cx('AddressSelector__toggle')}
        onClick={handleIntercomAndPorchToggle}
        onKeyDown={e => e.key === 'Enter' && handleIntercomAndPorchToggle()}
        role="button"
        tabIndex={0}
      >
        {intl.formatMessage({ id: 'button.addIntercom' })}
        <ToggleArrow className={cx('AddressSelector__arrow')} isOpen={isIntercomAndPorchOpened} />
      </div>
      <Collapse isOpen={isIntercomAndPorchOpened}>
        <div className={cx('AddressSelector__row')}>
          {/* Домофон */}
          <InputField
            name="intercom"
            value={address.intercom}
            onChange={handleAddressOnChange}
            placeholder={intl.formatMessage({ id: 'field.intercom.label' })}
          />
          {/* Подъезд */}
          <InputField
            name="porch"
            value={address.porch}
            onChange={handleAddressOnChange}
            placeholder={intl.formatMessage({ id: 'field.porch.label' })}
          />
        </div>
      </Collapse>
      {isButtonExist && (
        <Button
          buttonRef={buttonRef}
          className={cx('AddressSelector__accept')}
          onClick={handleOnAccept}
          disabled={!address.address_in_line}
          isLoading={isLoading}
        >
          {intl.formatMessage({ id: 'button.accept' })}
        </Button>
      )}
    </form>
  );
}

AddressSelector.defaultProps = {
  className: '',
  onSuccessCallback: undefined,
};

AddressSelector.propTypes = {
  className: PropTypes.string,
  onSuccessCallback: PropTypes.func,
};
